import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

import { supabase } from "@/integrations/supabase/client";
import { getUpcomingMeetings, type Meeting } from "@/lib/upcomingMeetings";
import { fetchEnrollmentData, enrollmentFor } from "@/lib/enrollment";
import { ExecutivePage, PageHero } from "@/components/executive/system";
import { ChevronRight } from "lucide-react";
import { EnrollmentDiagnostic } from "@/components/admin/EnrollmentDiagnostic";
import { PartnerPaymentsSection } from "@/components/admin/payroll/PartnerPaymentsSection";
import { LIVE_ASSIGNMENT_STATUSES } from "@/lib/staffing";

/* ------------------------------------------------------------------ */
/*  Operations Home — Task Dashboard                                   */
/*  Show the actual work. Every section disappears when complete.      */
/* ------------------------------------------------------------------ */

/* ---------- Types ---------- */

interface TodayClass extends Meeting {
  instructorName: string | null;
  /** null = enrollment could not be determined (query failed). */
  enrolled: number | null;
}

interface PayrollDueRow {
  id: string;
  instructor_id: string;
  instructor_name: string;
  amount_cents: number;
  is_volunteer: boolean;
  needs_review?: boolean;
  pay_date: string;
}

interface CustomPayrollRationale {
  title: string;
  rate: string;
  meetings_in_period: number;
  coverage: string;
  payment_schedule: string;
  total_display: string;
  explanation: string;
}

interface CustomPayrollBucket {
  config_id: string;
  label: string;
  pay_date: string;
  payment_method: string | null;
  instructor: { id: string; name: string };
  computed_amount_cents: number;
  sessions_count: number;
  needs_review: boolean;
  rationale?: CustomPayrollRationale | null;
}

interface MissingDocsGroup {
  label: string;
  instructors: { id: string; name: string }[];
}

interface RegistrationReviewRow {
  id: string;
  parent_name: string;
  class_id: string;
  class_title: string;
  payment_status: string;
  created_at: string;
}

interface InterestClump {
  class_id: string;
  class_title: string;
  families: number;
}

interface UpcomingEvent {
  id: string;
  title: string;
  event_date: string;
  start_time: string | null;
  location: string | null;
}

interface DashboardData {
  todayClasses: TodayClass[];
  payroll: { periodPayDate: string | null; rows: PayrollDueRow[] };
  customPayrollBuckets: CustomPayrollBucket[];
  missingDocs: MissingDocsGroup[];
  registrations: RegistrationReviewRow[];
  interest: InterestClump[];
  events: UpcomingEvent[];
  loading: boolean;
  error: string | null;
}

/* ---------- Helpers ---------- */

const DAY_ORDER = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}
function friendlyDate(iso: string) {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}
function friendlyTime(iso: string | null) {
  if (!iso) return "";
  // "HH:MM:SS" or "HH:MM"
  const [h, m] = iso.split(":").map(Number);
  if (Number.isNaN(h)) return "";
  const am = h < 12;
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m ?? 0).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

/* ---------- Data hook ---------- */

function useDashboardData(authReady: boolean): DashboardData {
  const [state, setState] = useState<DashboardData>({
    todayClasses: [],
    payroll: { periodPayDate: null, rows: [] },
    customPayrollBuckets: [],
    missingDocs: [],
    registrations: [],
    interest: [],
    events: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    // Never request protected admin data before the session + admin role are
    // hydrated; payroll-engine would answer 400 not_signed_in.
    if (!authReady) return;
    let cancelled = false;

    (async () => {
      try {
        const now = new Date();
        const startToday = new Date(now);
        startToday.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startToday);
        endOfWeek.setDate(endOfWeek.getDate() + 7);

        const todayIso = startToday.toISOString().slice(0, 10);
        const in30 = new Date(startToday);
        in30.setDate(in30.getDate() + 30);
        const last30 = new Date(startToday);
        last30.setDate(last30.getDate() - 30);

        // Kick off parallel queries.
        const meetingsP = getUpcomingMeetings(startToday, endOfWeek);

        const assignmentsP = supabase
          .from("instructor_class_assignments")
          .select("class_slug, instructor_id, status, start_date, end_date")
          .in("status", [...LIVE_ASSIGNMENT_STATUSES]);

        const instructorsP = supabase
          .from("instructors")
          .select(
            "id, full_name, preferred_name, status, pay_rate_type, w9_status, teaching_agreement_signed, background_check_completed, independent_contractor_ack",
          );

        // Enrollment — use the canonical resolver shared by the Operations
        // Board, Registrations page, roster drawer, and check-class-capacity
        // edge function. Never compute enrollment locally.
        const enrollmentP = fetchEnrollmentData().then(
          (d) => ({ ok: true as const, payments: d.payments, children: d.children }),
          () => ({ ok: false as const, payments: [] as never[], children: [] as never[] }),
        );

        // Upcoming payroll period + line items.
        const periodP = supabase
          .from("payroll_periods")
          .select("id, pay_date, status")
          .eq("status", "upcoming")
          .order("pay_date", { ascending: true })
          .limit(1)
          .maybeSingle();

        // Recent registrations to review.
        const recentRegsP = supabase
          .from("class_payments")
          .select("id, class_id, adult_name, payment_status, created_at")
          .neq("hidden", true)
          .order("created_at", { ascending: false })
          .limit(8);

        // Interest last 30 days.
        const interestP = supabase
          .from("class_interest")
          .select("class_id, email")
          .gte("created_at", last30.toISOString());

        // Upcoming events.
        const eventsP = supabase
          .from("open_house_events")
          .select("id, title, event_date, start_time, location_name")
          .gte("event_date", todayIso)
          .neq("status", "cancelled")
          .order("event_date", { ascending: true })
          .limit(5);

        const [meetings, assignmentsR, instructorsR, enrollmentR, periodR, regsR, interestR, eventsR] =
          await Promise.all([
            meetingsP,
            assignmentsP,
            instructorsP,
            enrollmentP,
            periodP,
            recentRegsP,
            interestP,
            eventsP,
          ]);

        // Build instructor lookup by id.
        const instructorsById = new Map<string, any>();
        for (const i of instructorsR.data ?? []) instructorsById.set(i.id, i);

        // Slug → instructor name (first active assignment covering today wins).
        const instrBySlug = new Map<string, string>();
        for (const a of assignmentsR.data ?? []) {
          if (!a.class_slug) continue;
          const startOk = !a.start_date || a.start_date <= todayIso;
          const endOk = !a.end_date || a.end_date >= todayIso;
          if (!startOk || !endOk) continue;
          if (instrBySlug.has(a.class_slug)) continue;
          const ins = instructorsById.get(a.instructor_id);
          if (!ins) continue;
          const name = ins.preferred_name || ins.full_name;
          if (name) instrBySlug.set(a.class_slug, name);
        }

        // Enrollment for each meeting = canonical resolver, scoped to the
        // meeting's session label. Same math as Operations Board, Registrations
        // page, roster drawer, and the check-class-capacity edge function.
        const enrollmentOk = enrollmentR.ok;
        const payments = enrollmentR.payments;
        const children = enrollmentR.children;
        const todayClasses: TodayClass[] = meetings.map((m) => ({
          ...m,
          instructorName: instrBySlug.get(m.classSlug) ?? null,
          enrolled: enrollmentOk
            ? enrollmentFor(payments, m.classId, m.sessionLabel || null, children)
            : null,
        }));

        // Payroll due — hydrate line items for the upcoming period, then filter
        // out inactive instructors, explicit volunteers, and anyone on a custom
        // pay schedule (they get their own bucket below).
        let payrollRows: PayrollDueRow[] = [];
        let periodPayDate: string | null = null;
        const period = periodR.data as { id: string; pay_date: string } | null;

        // Load compensation overrides once — used to drive volunteer + custom exclusions.
        const { data: overrides } = await supabase
          .from("instructor_compensation_config")
          .select("instructor_id, class_slug, compensation_source, pay_schedule, effective_start_date, effective_end_date");
        const todayIso2 = todayIso;
        const volunteerIds = new Set<string>();
        const customIds = new Set<string>();
        for (const o of overrides ?? []) {
          const startsOk = !o.effective_start_date || String(o.effective_start_date) <= todayIso2;
          const endsOk = !o.effective_end_date || String(o.effective_end_date) >= todayIso2;
          if (!startsOk || !endsOk) continue;
          if (o.pay_schedule === "custom" && o.instructor_id) customIds.add(o.instructor_id);
          if (o.compensation_source === "volunteer" && !o.class_slug && o.instructor_id) {
            volunteerIds.add(o.instructor_id);
          }
        }

        {
          // Single source of truth: ask the payroll engine for the same numbers
          // the Payroll page shows (honors per-period overrides, custom pay
          // schedules, exclusions, volunteers, outstanding/overdue periods).
          try {
            const { data: sumResp } = await supabase.functions.invoke("payroll-engine", {
              body: { action: "upcoming_summary" },
            });
            const engineItems = (sumResp as any)?.line_items ?? [];
            // The engine decides WHICH period is outstanding — never the client.
            const enginePeriodId = (sumResp as any)?.period_id ?? period?.id ?? null;
            periodPayDate = (sumResp as any)?.period?.pay_date ?? period?.pay_date ?? null;

            const persistedById = new Map<string, any>();
            if (enginePeriodId) {
              const { data: lines } = await supabase
                .from("payroll_line_items")
                .select("id, instructor_id, status")
                .eq("period_id", enginePeriodId)
                .neq("status", "confirmed_paid")
                .neq("status", "skipped");
              for (const l of lines ?? []) persistedById.set(l.instructor_id as string, l);
            }

            for (const li of engineItems) {
              const ins = instructorsById.get(li.instructor_id as string);
              if (!ins) continue;
              if (String(ins.status ?? "").toLowerCase() !== "active") continue;
              if (volunteerIds.has(li.instructor_id)) continue;
              if (customIds.has(li.instructor_id)) continue;
              const persisted = persistedById.get(li.instructor_id);
              payrollRows.push({
                id: (li.line_item_id as string) ?? (persisted?.id as string) ?? li.instructor_id,
                instructor_id: li.instructor_id,
                instructor_name: li.instructor_full_name || li.instructor_name || ins.preferred_name || ins.full_name || "Unnamed",
                amount_cents: li.computed_amount_cents ?? 0,
                is_volunteer: false,
                needs_review: li.status === "needs_review",
                pay_date: periodPayDate ?? "",
              });
            }
          } catch (e) {
            console.warn("payroll-engine upcoming_summary failed, falling back to DB", e);
            periodPayDate = period?.pay_date ?? null;
          }
          payrollRows.sort((a, b) => a.instructor_name.localeCompare(b.instructor_name));
        }



        // Custom-schedule payroll buckets — one section per active custom config
        // whose next_pay_date is due within the horizon.
        let customPayrollBuckets: CustomPayrollBucket[] = [];
        try {
          const { data: customResp } = await supabase.functions.invoke("payroll-engine", {
            body: { action: "custom_pay_due" },
          });
          if (customResp && Array.isArray((customResp as any).buckets)) {
            customPayrollBuckets = ((customResp as any).buckets as any[])
              .filter((b) => !volunteerIds.has(b.instructor?.id))
              .map((b) => ({
                config_id: b.config_id,
                label: b.label,
                pay_date: b.pay_date,
                payment_method: b.payment_method ?? null,
                instructor: b.instructor,
                computed_amount_cents: b.computed_amount_cents ?? 0,
                sessions_count: b.sessions_count ?? 0,
                needs_review: !!b.needs_review,
                rationale: b.rationale ?? null,
              }));
          }
        } catch {
          // Non-fatal — dashboard still renders standard payroll.
        }

        // Missing documents — walk active instructors.
        const groups: Record<string, { id: string; name: string }[]> = {
          "Needs W-9": [],
          "Needs Background Check": [],
          "Needs Teaching Agreement": [],
        };
        for (const ins of instructorsR.data ?? []) {
          const status = String(ins.status ?? "").toLowerCase();
          // Active teachers need everything; volunteers skip tax forms/W-9
          // but still need the teaching agreement and a current background
          // check. Standards of Conduct is covered by the teaching agreement.
          const isActive = status === "active";
          const isVolunteer = status === "volunteer";
          if (!isActive && !isVolunteer) continue;
          const name = ins.preferred_name || ins.full_name || "Unnamed";
          const row = { id: ins.id as string, name };
          if (isActive) {
            const w9 = String(ins.w9_status ?? "").toLowerCase();
            if (w9 !== "received" && w9 !== "on_file" && w9 !== "current") {
              groups["Needs W-9"].push(row);
            }
          }
          if (!ins.background_check_completed) groups["Needs Background Check"].push(row);
          if (!ins.teaching_agreement_signed) groups["Needs Teaching Agreement"].push(row);
        }
        const missingDocs: MissingDocsGroup[] = Object.entries(groups)
          .filter(([, list]) => list.length > 0)
          .map(([label, list]) => ({ label, instructors: list }));

        // Registrations to review — map class_id → title using meetings + payments.
        const titleByKey = new Map<string, string>();
        for (const m of meetings) {
          titleByKey.set(m.classSlug, m.title);
          titleByKey.set(m.classId, m.title);
        }
        const registrations: RegistrationReviewRow[] = (regsR.data ?? []).map((r: any) => ({
          id: r.id,
          class_id: r.class_id,
          class_title: titleByKey.get(r.class_id) ?? r.class_id,
          parent_name: r.adult_name ?? "Someone",
          payment_status: r.payment_status ?? "unknown",
          created_at: r.created_at,
        }));

        // Interest grouping.
        const interestMap = new Map<string, Set<string>>();
        for (const row of (interestR.data ?? []) as Array<{ class_id: string; email: string }>) {
          if (!row.class_id) continue;
          const set = interestMap.get(row.class_id) ?? new Set<string>();
          set.add((row.email ?? "").toLowerCase());
          interestMap.set(row.class_id, set);
        }
        const interest: InterestClump[] = Array.from(interestMap.entries())
          .map(([class_id, set]) => ({
            class_id,
            class_title: titleByKey.get(class_id) ?? class_id,
            families: set.size,
          }))
          .sort((a, b) => b.families - a.families);

        const events: UpcomingEvent[] = (eventsR.data ?? []).map((e: any) => ({
          id: e.id,
          title: e.title,
          event_date: e.event_date,
          start_time: e.start_time,
          location: e.location_name,
        }));

        if (cancelled) return;
        setState({
          todayClasses,
          payroll: { periodPayDate, rows: payrollRows },
          customPayrollBuckets,
          missingDocs,
          registrations,
          interest,
          events,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: err instanceof Error ? err.message : "Unable to load dashboard",
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authReady]);

  return state;
}

/* ---------- Section shell ---------- */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="pt-8">
    <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-3">
      {title}
    </h2>
    {children}
  </section>
);

/* ---------- Page ---------- */

const UnifiedDashboard = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const data = useDashboardData(!authLoading && !!user && isAdmin);
  const showDiag =
    isAdmin && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("diag") === "enrollment";

  const firstName = useMemo(() => {
    const meta = (user?.user_metadata as any) ?? {};
    const raw = meta.full_name || meta.name || user?.email?.split("@")[0] || "Savanna";
    return String(raw).split(/[\s@]/)[0];
  }, [user]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return "Welcome back";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  const [checkedPayroll, setCheckedPayroll] = useState<Set<string>>(new Set());

  // Group today's classes by weekday, preserving date order.
  const classesByDay = useMemo(() => {
    const map = new Map<string, TodayClass[]>();
    for (const c of data.todayClasses) {
      const key = `${c.date}|${c.weekday}`;
      const list = map.get(key) ?? [];
      list.push(c);
      map.set(key, list);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({
        date: key.split("|")[0],
        weekday: key.split("|")[1],
        items,
      }));
  }, [data.todayClasses]);

  const visiblePayroll = data.payroll.rows.filter((r) => !checkedPayroll.has(r.id));
  const visibleCustomBuckets = data.customPayrollBuckets;

  const nothingToDo =
    !data.loading &&
    classesByDay.length === 0 &&
    visiblePayroll.length === 0 &&
    visibleCustomBuckets.length === 0 &&
    data.missingDocs.length === 0 &&
    data.registrations.length === 0 &&
    data.interest.length === 0 &&
    data.events.length === 0;

  return (
    <ExecutivePage section="dashboard" hideDock maxWidthClassName="max-w-3xl">
      <PageHero
        eyebrow={<span className="opacity-0 select-none">.</span>}
        title={<>{greeting}, {firstName}.</>}
        subtitle={<>Here's your work for today.</>}
      />

      {data.loading && (
        <div className="pt-6 space-y-3">
          <div className="h-24 rounded-md bg-muted/30 animate-pulse" />
          <div className="h-24 rounded-md bg-muted/30 animate-pulse" />
        </div>
      )}

      {data.error && !data.loading && (
        <div className="mt-6 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {data.error}
        </div>
      )}

      {!data.loading && nothingToDo && (
        <div className="mt-8 rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">
            Nothing on your list. Enjoy the quiet.
          </p>
        </div>
      )}

      {/* Section 1 — Today's Classes (this week) */}
      {classesByDay.length > 0 && (
        <Section title="This Week's Classes">
          <div className="space-y-5">
            {classesByDay.map((day) => (
              <div key={day.date}>
                <p className="text-sm font-semibold text-foreground mb-2">
                  {friendlyDate(day.date)}
                </p>
                <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
                  {day.items.map((c, idx) => (
                    <li key={`${c.classSlug}-${idx}`}>
                      <Link
                        to={`/admin/registrations?class=${encodeURIComponent(c.classSlug)}`}
                        className="group flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-medium text-foreground break-words">{c.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {c.timeDisplay || "Time TBD"}
                            {c.instructorName && <> · {c.instructorName}</>}
                            {" · "}{c.enrolled === null ? "Enrollment unavailable" : `${c.enrolled} enrolled`}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Section 2 — Payroll Due */}
      {visiblePayroll.length > 0 && (
        <Section
          title={
            data.payroll.periodPayDate
              ? `Payroll Due ${new Date(data.payroll.periodPayDate + "T12:00:00").toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}`
              : "Payroll Due"
          }
        >
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            {visiblePayroll.map((row) => (
              <li key={row.id} className="flex items-center gap-3 px-4 py-3">
                <input
                  type="checkbox"
                  aria-label={`Confirm payment for ${row.instructor_name}`}
                  className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                  onChange={(e) => {
                    if (!e.target.checked) return;
                    // Optimistically hide the row and launch the payroll confirmation flow.
                    setCheckedPayroll((s) => new Set(s).add(row.id));
                    window.location.assign(`/admin/payroll?confirm=${row.id}`);
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-medium text-foreground break-words">
                    {row.instructor_name}
                  </p>
                </div>
                <span className={`text-sm ${row.is_volunteer ? "text-muted-foreground italic" : "text-foreground font-medium"}`}>
                  {row.is_volunteer ? "Volunteer" : formatMoney(row.amount_cents)}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Section 2b — Custom-schedule payroll buckets (Coach C etc.) */}
      {visibleCustomBuckets.map((bucket) => (
        <Section
          key={bucket.config_id}
          title={`${bucket.label} – ${new Date(bucket.pay_date + "T12:00:00").toLocaleDateString(undefined, {
            weekday: "long", month: "short", day: "numeric",
          })}`}
        >
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            <li className="flex items-center gap-3 px-4 py-3">
              <input
                type="checkbox"
                aria-label={`Confirm payment for ${bucket.instructor.name}`}
                className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                onChange={(e) => {
                  if (!e.target.checked) return;
                  window.location.assign(`/admin/payroll?custom=${bucket.config_id}`);
                }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-medium text-foreground break-words">
                  {bucket.instructor.name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {bucket.sessions_count} {bucket.sessions_count === 1 ? "meeting" : "meetings"}
                  {bucket.payment_method && <> · {bucket.payment_method}</>}
                </p>
              </div>
              <span className={`text-sm ${bucket.needs_review ? "text-amber-600 italic" : "text-foreground font-medium"}`}>
                {bucket.needs_review ? "Payroll needs review" : formatMoney(bucket.computed_amount_cents)}
              </span>
            </li>
            {bucket.rationale && (
              <li className="px-4 py-3 bg-muted/20 text-xs space-y-1">
                <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
                  <span className="text-muted-foreground">Rate</span>
                  <span className="text-foreground">{bucket.rationale.rate}</span>
                  <span className="text-muted-foreground">Meetings in covered period</span>
                  <span className="text-foreground">{bucket.rationale.meetings_in_period}</span>
                  <span className="text-muted-foreground">Coverage</span>
                  <span className="text-foreground">{bucket.rationale.coverage}</span>
                  <span className="text-muted-foreground">Payment schedule</span>
                  <span className="text-foreground">{bucket.rationale.payment_schedule}</span>
                  <span className="text-muted-foreground">Total</span>
                  <span className="text-foreground font-medium">{bucket.rationale.total_display}</span>
                </div>
                <p className="text-muted-foreground italic pt-1">{bucket.rationale.explanation}</p>
              </li>
            )}
          </ul>
        </Section>
      ))}

      {/* Section 2c — Partner Payments (revenue-share payees like Grace) */}
      <PartnerPaymentsSection />

      {/* Section 3 — Instructor Documents */}

      {data.missingDocs.length > 0 && (
        <Section title="Instructor Documents">
          <div className="space-y-5">
            {data.missingDocs.map((group) => (
              <div key={group.label}>
                <p className="text-sm font-semibold text-foreground mb-2">{group.label}</p>
                <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
                  {group.instructors.map((ins) => (
                    <li key={ins.id}>
                      <Link
                        to={`/admin/instructors/${ins.id}`}
                        className="group flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors"
                      >
                        <span className="flex-1 text-[15px] text-foreground">{ins.name}</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Section 4 — Registrations to Review */}
      {data.registrations.length > 0 && (
        <Section title="Registrations to Review">
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            {data.registrations.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/admin/registrations?highlight=${r.id}`}
                  className="group flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium text-foreground break-words">{r.parent_name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 break-words">
                      {r.class_title} · {r.payment_status === "paid" ? "Paid" : r.payment_status === "pending" ? "Needs approval" : r.payment_status}
                    </p>
                  </div>
                  <span className="text-xs text-primary opacity-0 group-hover:opacity-100">Open</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Section 5 — Interest Forms */}
      {data.interest.length > 0 && (
        <Section title="Interest Forms">
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            {data.interest.map((row) => (
              <li key={row.class_id}>
                <Link
                  to={`/admin/classes?interest=${encodeURIComponent(row.class_id)}`}
                  className="group flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium text-foreground break-words">{row.class_title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {row.families} {row.families === 1 ? "family" : "families"}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Section 6 — Upcoming Events */}
      {data.events.length > 0 && (
        <Section title="Upcoming Events">
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            {data.events.map((e) => (
              <li key={e.id}>
                <Link
                  to="/admin/open-house"
                  className="group flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium text-foreground break-words">{e.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {friendlyDate(e.event_date)}
                      {e.start_time && <> · {friendlyTime(e.start_time)}</>}
                      {e.location && <> · {e.location}</>}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {showDiag && <EnrollmentDiagnostic />}
    </ExecutivePage>
  );
};

export default UnifiedDashboard;
